import { getReferencialData } from '@src/api/Referencial';

import BackButton from "@src/components/_shared/BackButton";
import Container from "@src/components/_shared/Container";
import BlockTitle from "@src/components/_shared/UI/BlockTitle";
import CountryDropdown from '@src/components/_shared/UI/CountryDropdownList';
import Input from '@src/components/_shared/UI/Input';
import useNavigationStore from "@src/stores/navigationStore";
import useRegistrationStore from "@src/stores/registrationStore";
import { useMutation, useQuery } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import { birthSchema } from '@src/utilities/schemas';
import CitySearch from '@src/components/_shared/UI/CitySearch';
import Loader from '@src/components/_shared/UI/Loader';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import { personUpdate } from '@src/api/Registration';
import { dateToTimestamp, formatDate, timestampToDate, handleApiError } from '@src/utilities/functions';

export default function Birth() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();
    const [showCountrySearch, setShowCountrySearch] = useState(!!registration.person.birth.date);
    const [cityNotFound, setCityNotFound] = useState<boolean>(false);

    const { data, isLoading } = useQuery({
        queryKey: [`getCountriesData`],
        queryFn: () => getReferencialData({type: 'countries'})
    })

    const { mutate, isPending } = useMutation({
        mutationFn: (value: any) => personUpdate({key: 'birth', value, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id}),
        onSuccess: () => setBlock('LegalCapacity'),
        onError: (error: any) => handleApiError(error)
    });

    const _onGoBack = () => {
        setSection(2)
        setBlock('FiscalAddress')
    };

    const formattedInitialDate = registration.person.birth.date
        ? timestampToDate(Number(registration.person.birth.date))
        : '';

    return (
        <Container>
            <BackButton onClick={_onGoBack}/>
            <BlockTitle title="Données de naissance" />
            <Subtitle content="Veuillez renseigner les informations suivantes" />
            {isLoading ?
                <Loader type="SPINNER" />
                :
                <Formik
                    initialValues={{
                        date: formattedInitialDate,
                        place: {
                            city: registration.person.birth.place.city,
                            country: registration.person.birth.place.country,
                            zipcode: registration.person.birth.place.zipcode
                        }
                    }}
                    onSubmit={(values) => {
                        const timestampDate = dateToTimestamp(values.date);
                        const updatedValues = { ...values, date: timestampDate };
                        setField('person.birth', updatedValues);
                        mutate(updatedValues);
                    }}
                    validationSchema={birthSchema}
                >
                    {({ handleChange, handleSubmit, values, errors, setFieldValue, isValid }) => {
                        const shouldShowCityAndZipcodeInputs = values.place.country && (cityNotFound || values.place.country !== 'FR');
                        return (
                            <Form id="birthForm" autoComplete="off" className="registerForm">
                                <Input
                                    title="Date de naissance"
                                    name="birthDate"
                                    id="birthDate"
                                    width="100%"
                                    type="text"
                                    placeholder="JJ/MM/AAAA"
                                    maxLength={10}
                                    value={values.date}
                                    onChange={(e: any) => {
                                        const inputValue = e.target.value;
                                        if (e.nativeEvent.inputType === 'Backspace') {
                                            handleChange(e);
                                        } else {
                                            const formattedValue = formatDate(inputValue);
                                            handleChange({
                                                target: {
                                                    name: 'date',
                                                    value: formattedValue
                                                }
                                            });
                                            if (formattedValue.length === 10) {
                                                setShowCountrySearch(true);
                                            }
                                        }
                                    }}
                                    hasError={(values.date.length === 10 && !!errors.date) as boolean}
                                    clearInput={() => handleChange('date')('')}
                                    errorMessage={errors.date}
                                />
                                {data && showCountrySearch && !errors.date ?
                                    <CountryDropdown
                                        title="Pays de naissance"
                                        width="100%"
                                        options={data}
                                        selected={registration.person.birth.place.country}
                                        onSelect={(value: string) => {
                                            handleChange('place.country')(value);
                                            handleChange('place.city')('');
                                            handleChange('place.zipcode')('');
                                            setCityNotFound(false);
                                        }}
                                    />
                                    : null}
                                {values.place.country === 'FR' && !cityNotFound && !errors.date ?
                                    <CitySearch
                                        title="Recherchez votre ville de naissance"
                                        initialValue={
                                            registration.person.birth.place.city
                                                ? `${registration.person.birth.place.zipcode} ${registration.person.birth.place.city}`
                                                : ''
                                        }
                                        _onSelectExistingCity={(value: { city: string; zipcode: string }) => {
                                            setFieldValue('place.city', value.city);
                                            setFieldValue('place.zipcode', value.zipcode);
                                        }}
                                        clearInput={() => {
                                            handleChange('place.city')('');
                                            handleChange('place.zipcode')('');
                                        }}
                                        setCityNotFound={setCityNotFound}
                                    />
                                    : null}
                                {shouldShowCityAndZipcodeInputs ?
                                    <>
                                        <Input
                                            title="Code postal"
                                            name="birthZipcode"
                                            id="birthZipcode"
                                            width="100%"
                                            type="text"
                                            placeholder="Code postal"
                                            value={values.place.zipcode}
                                            onChange={handleChange('place.zipcode')}
                                            clearInput={() => handleChange('place.zipcode')('')}
                                        />
                                        <Input
                                            title="Ville"
                                            name="birthCity"
                                            id="birthCity"
                                            width="100%"
                                            type="text"
                                            placeholder="Ville"
                                            value={values.place.city}
                                            onChange={handleChange('place.city')}
                                            clearInput={() => handleChange('place.city')('')}
                                        />
                                    </>
                                    : null }
                                {values.place.country && values.date && values.place.city && values.place.zipcode &&isValid ? (
                                    <ValidateButton onClick={handleSubmit} title="Continuer" onLoading={isPending} disabled={isPending} />
                                ) : null}
                            </Form>
                        );
                    }}
                </Formik>
            }
        </Container>
    )

}
